type BaseCategoryType = {
  id: string;
  name: string;
  count: number;
  order: number;
  slug?: string;
  link?: string;
  content?: Array<{
    id?: string;
    title?: string;
  }>;
};

export type CategoryType = BaseCategoryType & {
  children?: {
    __id?: string;
    edges: CategoryType[];
  } | null;
  parent?: CategoryType | null;
};

type ParentTravelsalOptionsType<T> = {
  reverse?: boolean;
  callback: (category: CategoryType) => T;
};
export const parentTraversal = <T>(category: CategoryType, opts: ParentTravelsalOptionsType<T>): T[] => {
  const ret: T[] = [];

  if (opts?.reverse) {
    if (category?.parent) ret.push(...parentTraversal(category.parent, opts));
    ret.push(opts.callback(category));
    return ret;
  }

  ret.push(opts.callback(category));
  if (category?.parent) ret.push(...parentTraversal(category.parent, opts));
  return ret;
};

// traverse a category from the root through each child
// a callback is called on each category and the return value is pushed to an array
export const categoryTraversal = <T>(
  category: Partial<CategoryType>,
  callback: (cat: Partial<CategoryType>) => T
): T[] => {
  const ret: T[] = [];

  // if we are traversing an array of categories with a node property
  if (Array.isArray(category)) {
    category.forEach((cat) => {
      let node = cat;
      if (cat?.node) node = cat.node;

      ret.push(callback(node));
      if (node?.children) ret.push(...categoryTraversal(node.children.edges, callback));
    });
    return ret;
  }

  ret.push(callback(category));
  if (category?.children)
    category.children?.edges?.map((child) => {
      ret.push(callback(child));
      categoryTraversal(child, callback);
    });
  return ret;
};

// traverse a category from the root through each child
const childTraversal = (_category: CategoryType, callback: (cat: CategoryType) => void, first = true) => {
  let category = _category;
  if (first) {
    category = { ..._category };
    callback(category); // callback on the first call
  }
  if (category?.children) {
    category.children.edges.forEach((child) => {
      callback(child);
      childTraversal(child, callback, false);
    });
  }

  return category;
};

// function checks if a name input is a duplicate name in all the categories
export const checkDuplicateName = (categories: CategoryType[], name: string): boolean => {
  let duplicate = false;

  categories.forEach((category) => {
    if (category.name.toLowerCase() === name.toLowerCase()) duplicate = true;
    if (duplicate === true) return true;

    return childTraversal(category, (child) => {
      if (duplicate === true) return true;
      if (child.name.toLowerCase() === name.toLowerCase()) duplicate = true;
    });
  });

  return duplicate;
};
